import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { 
  Feather, 
  Library, 
  User, 
  LogOut, 
  ChevronRight,
  BookOpen,
  Clock,
  Play,
  Heart
} from 'lucide-react';

import { API } from '../config';

export default function LibraryPage() {
  const { user, token, isSubscribed, logout } = useAuth();
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [readingStats, setReadingStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const seriesRes = await axios.get(`${API}/series`, { 
        headers,
        withCredentials: true 
      });
      setSeries(seriesRes.data);

      // Try to get reading stats (will work with cookie auth too)
      try {
        const statsRes = await axios.get(`${API}/progress/stats`, { 
          headers,
          withCredentials: true 
        });
        setReadingStats(statsRes.data);
      } catch (statsError) {
        // Stats may fail if not authenticated, that's ok
        console.log('Could not fetch reading stats');
      }

      // Try to get favorites
      try {
        const favRes = await axios.get(`${API}/favorites`, { 
          headers,
          withCredentials: true 
        });
        setFavorites(favRes.data);
      } catch (favError) {
        console.log('Could not fetch favorites');
      }
    } catch (error) {
      console.error('Error fetching library data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getSeriesProgress = (seriesId) => {
    if (!readingStats) return null;
    return readingStats.series_progress.find(p => p.series_id === seriesId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <div className="text-center">
          <Feather className="w-8 h-8 text-wine mx-auto mb-4 animate-pulse" />
          <p className="font-lora text-charcoal-muted">Loading your library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-parchment/95 backdrop-blur-sm border-b border-[#E5E0D0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_ink-nostalgia/artifacts/xhguxwkc_34799.png"
              alt="Unspoken Hearts"
              className="h-10"
            />
          </Link>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/account" 
              className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors"
              data-testid="account-link"
            >
              <User className="w-5 h-5" />
              <span className="font-lora hidden sm:inline">Account</span>
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-charcoal-muted hover:text-charcoal"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <header className="mb-10 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-wine/10 rounded-full flex items-center justify-center">
              <Library className="w-6 h-6 text-wine" />
            </div>
            <div>
              <h1 className="font-playfair text-4xl md:text-5xl text-charcoal">Your Library</h1>
            </div>
          </div>
          <p className="font-lora text-lg text-charcoal-muted max-w-2xl">
            {isSubscribed 
              ? "Explore our collection of love stories. Each series offers a unique journey through time and emotion."
              : "Subscribe to unlock all letters across our entire collection of love stories."
            }
          </p>
        </header>

        {/* Reading Stats Summary */}
        {readingStats && isSubscribed && (
          <Card className="p-6 mb-10 bg-parchment-paper border-[#E5E0D0]" data-testid="reading-summary">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-lora text-sm text-charcoal-muted mb-1">Your Reading Journey</p>
                <p className="font-playfair text-2xl text-charcoal">
                  {readingStats.total_letters_read} letters read
                </p>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="font-playfair text-3xl text-wine">{readingStats.total_series}</p>
                  <p className="font-lora text-xs text-charcoal-muted">Series</p>
                </div>
                <div className="text-center">
                  <p className="font-playfair text-3xl text-sepia">
                    {readingStats.series_progress.reduce((acc, s) => acc + s.total_letters, 0)}
                  </p>
                  <p className="font-lora text-xs text-charcoal-muted">Total Letters</p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Subscribe CTA for non-subscribers */}
        {!isSubscribed && (
          <Card className="p-6 mb-10 bg-wine/5 border-wine/20" data-testid="subscribe-cta">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h2 className="font-playfair text-xl text-charcoal mb-2">Unlock the Full Experience</h2>
                <p className="font-lora text-charcoal-muted">
                  Subscribe to access all 104+ letters across our growing collection.
                </p>
              </div>
              <Button 
                onClick={() => navigate('/subscription')} 
                className="btn-primary whitespace-nowrap"
                data-testid="subscribe-btn"
              >
                Subscribe for $7/month
              </Button>
            </div>
          </Card>
        )}

        {/* Favorites Section */}
        {favorites.length > 0 && (
          <div className="mb-10" data-testid="favorites-section">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
              <h2 className="font-playfair text-2xl text-charcoal">Your Favorites</h2>
              <span className="font-lora text-sm text-charcoal-muted">({favorites.length})</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {favorites.slice(0, 6).map((fav) => (
                <Card
                  key={fav.letter_id}
                  className="p-4 bg-parchment-paper border-[#E5E0D0] hover:shadow-soft-paper hover:border-rose-200 transition-all cursor-pointer group"
                  onClick={() => navigate(`/series/${fav.series_id}/letter/${fav.week_number}`)}
                  data-testid={`favorite-card-${fav.letter_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-lora text-xs text-wine mb-0.5">{fav.series_title}</p>
                      <h3 className="font-playfair text-sm text-charcoal group-hover:text-wine transition-colors truncate">
                        {fav.title}
                      </h3>
                      <p className="font-lora text-xs text-charcoal-muted">Week {fav.week_number}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-charcoal-light group-hover:text-wine flex-shrink-0" />
                  </div>
                </Card>
              ))}
            </div>
            {favorites.length > 6 && (
              <p className="text-center mt-4 font-lora text-sm text-charcoal-muted">
                + {favorites.length - 6} more favorites
              </p>
            )}
          </div>
        )}

        {/* Series Grid */}
        <div className="space-y-6" data-testid="series-grid">
          {series.map((s, index) => {
            const progress = getSeriesProgress(s.series_id);
            const progressPercent = progress 
              ? (progress.letters_read / progress.total_letters) * 100 
              : 0;

            return (
              <Card
                key={s.series_id}
                className="p-6 bg-parchment border-[#E5E0D0] hover:shadow-soft-paper hover:border-sepia/30 transition-all cursor-pointer group"
                onClick={() => navigate(`/series/${s.series_id}`)}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`series-card-${s.series_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  {/* Series Icon */}
                  <div className="w-20 h-20 bg-wine/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-wine/20 transition-colors">
                    <BookOpen className="w-10 h-10 text-wine" />
                  </div>

                  {/* Series Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-lora text-sm text-wine mb-1">{s.era}</p>
                        <h2 className="font-playfair text-2xl text-charcoal group-hover:text-wine transition-colors">
                          {s.title}
                        </h2>
                      </div>
                      <ChevronRight className="w-6 h-6 text-charcoal-light group-hover:text-wine transition-colors flex-shrink-0" />
                    </div>
                    
                    <p className="font-lora text-charcoal-muted mb-4 line-clamp-2">
                      {s.description}
                    </p>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      {/* Stats */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5 text-charcoal-muted">
                          <BookOpen className="w-4 h-4" />
                          <span className="font-lora text-sm">{s.letter_count} letters</span>
                        </div>
                        <span className="font-dancing text-sepia">
                          {s.protagonist_1} & {s.protagonist_2}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      {progress && isSubscribed && (
                        <div className="flex-1 max-w-[200px]">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-lora text-xs text-charcoal-muted">
                              {progress.letters_read}/{progress.total_letters}
                            </span>
                            <span className="font-lora text-xs text-wine">
                              {Math.round(progressPercent)}%
                            </span>
                          </div>
                          <Progress value={progressPercent} className="h-1.5" />
                        </div>
                      )}
                    </div>

                    {/* Continue Reading */}
                    {progress?.last_read_letter && isSubscribed && (
                      <div className="mt-4 pt-4 border-t border-[#E5E0D0]">
                        <div className="flex items-center gap-2 text-wine">
                          <Play className="w-4 h-4" />
                          <span className="font-lora text-sm">
                            Continue from Week {progress.last_read_letter + 1}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Coming Soon */}
        <Card className="mt-8 p-6 bg-parchment-subtle/50 border-[#E5E0D0]/50 border-dashed" data-testid="coming-soon">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-parchment-paper rounded-xl flex items-center justify-center">
              <Clock className="w-8 h-8 text-charcoal-light" />
            </div>
            <div>
              <h3 className="font-playfair text-xl text-charcoal-muted">More Stories Coming Soon</h3>
              <p className="font-lora text-charcoal-light">
                New series are being crafted. Subscribe to be notified when they're available.
              </p>
            </div>
          </div>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E5E0D0] mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between">
          <p className="font-dancing text-lg text-sepia">"Moments preserved in ink"</p>
          <p className="font-lora text-sm text-charcoal-muted">© 2026 Unspoken Hearts</p>
        </div>
      </footer>
    </div>
  );
}
